import { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, Stack, Button } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import useTeacherStore from '@/store/teacherStore';
import { Loader } from '@/components/feedback/Loader';
import { SubjectBadge } from '@/features/teachers/components/SubjectBadge';
import { CalendarDays } from 'lucide-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function MyTimetablePage() {
  const { user } = useAuth();
  const fetchTeacherTimetable = useTeacherStore(s => s.fetchTeacherTimetable);
  const timetable = useTeacherStore(s => s.timetable);
  const loading = useTeacherStore(s => s.loading);
  
  const [selectedDay, setSelectedDay] = useState('Monday');

  useEffect(() => {
    if (user?.teacherId) {
      fetchTeacherTimetable(user.teacherId);
    }
  }, [user?.teacherId, fetchTeacherTimetable]);

  if (loading && timetable.length === 0) return <Loader />;

  const todayClasses = timetable.filter(t => t.day === selectedDay).sort((a, b) => a.period - b.period);

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            My Timetable
          </Typography>
          <Typography color="text.secondary">
            Your weekly class schedule
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 4, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} overflow="auto">
          {DAYS.map(day => (
            <Button
              key={day}
              variant={selectedDay === day ? 'contained' : 'outlined'}
              onClick={() => setSelectedDay(day)}
              sx={{ minWidth: 100 }}
            >
              {day}
            </Button>
          ))}
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {todayClasses.length > 0 ? (
          todayClasses.map(cls => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cls.id}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    Period {cls.period}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {cls.time}
                  </Typography>
                </Stack>
                <Typography variant="h5" mb={1} fontWeight={600}>
                  {cls.className} - {cls.sectionName}
                </Typography>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 1 }}>
                  <SubjectBadge subject={cls.subject} />
                  <Typography variant="body2" color="text.secondary">
                    {cls.room}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
              <CalendarDays size={48} opacity={0.5} style={{ marginBottom: 16 }} />
              <Typography>No classes scheduled for {selectedDay}.</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
